import type { Accessor } from "solid-js";
import { createSignal } from "solid-js";

type DragAndDropHandler = (event: DragEvent) => void;

export interface UseDragAndDropOptions {
  onFileDrop?: (file: File) => void;
  onFilesDrop?: (files: FileList) => void;
}

export interface UseDragAndDropReturn {
  isDragging: Accessor<boolean>;
  handleDragEnter: DragAndDropHandler;
  handleDragOver: DragAndDropHandler;
  handleDragLeave: DragAndDropHandler;
  handleDrop: DragAndDropHandler;
  resetDragState: () => void;
}

export function useDragAndDrop(
  options: UseDragAndDropOptions = {},
): UseDragAndDropReturn {
  const [isDragging, setIsDragging] = createSignal(false);

  const handleDragEnter: DragAndDropHandler = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragOver: DragAndDropHandler = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = "copy";
    }
  };

  const handleDragLeave: DragAndDropHandler = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const x = event.clientX;
    const y = event.clientY;

    if (
      x <= rect.left ||
      x >= rect.right ||
      y <= rect.top ||
      y >= rect.bottom
    ) {
      setIsDragging(false);
    }
  };

  const handleDrop: DragAndDropHandler = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);

    const files = event.dataTransfer?.files;
    const file = files?.[0];

    if (files && files.length > 0) {
      options.onFilesDrop?.(files);
    }

    if (file) {
      options.onFileDrop?.(file);
    }
  };

  const resetDragState = () => setIsDragging(false);

  return {
    isDragging,
    handleDragEnter,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    resetDragState,
  };
}
